import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Card, CardContent, CardActions, Grid, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Alert, CircularProgress, Stack,
} from '@mui/material';
import { Add, CardGiftcard, ManageAccounts, Lightbulb, Visibility, VisibilityOff } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import Layout from '../components/Layout';
import { apiService, errorMessage } from '../services/api';
import { MyGroup } from '../types';
import { validateGroupName } from '../utils/validators';
import { formatBudget, formatShortDate } from '../utils/formatters';

const StyledCard = styled(Card)({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
});

const statusLabel = (g: MyGroup) => {
  if (g.status === 'completed') return { label: 'Completed', color: 'default' as const };
  if (g.status === 'matched') return { label: 'Matched', color: 'success' as const };
  return { label: 'Setting up', color: 'warning' as const };
};

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState<MyGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const thisYear = new Date().getFullYear();
  const [form, setForm] = useState({
    name: '', year: thisYear, budget: '', exchangeDate: `${thisYear}-12-25`, exchangeLocation: '',
  });

  useEffect(() => {
    apiService.getMyGroups()
      .then(setGroups)
      .catch((e) => setError(errorMessage(e, 'Failed to load your groups')))
      .finally(() => setLoading(false));
  }, []);

  const handleCreate = async () => {
    const validationError = validateGroupName(form.name);
    if (validationError) { setError(validationError); return; }
    setCreating(true);
    setError(null);
    try {
      const group = await apiService.createGroup({
        name: form.name.trim(),
        year: form.year,
        budget: form.budget ? Number(form.budget) : null,
        exchangeDate: form.exchangeDate || null,
        exchangeLocation: form.exchangeLocation || null,
        settings: { autoExcludePreviousYears: true, surveyEnabled: true },
      });
      setCreateOpen(false);
      navigate(`/group/${group.id}`);
    } catch (e) {
      setError(errorMessage(e, 'Failed to create group'));
    } finally {
      setCreating(false);
    }
  };

  const participating = groups.filter((g) => g.isParticipant);
  const organizing = groups.filter((g) => g.isOrganizer);

  const renderGroup = (g: MyGroup, mode: 'participant' | 'organizer') => {
    const status = statusLabel(g);
    return (
      <Grid item xs={12} sm={6} md={4} key={`${mode}-${g.id}`}>
        <StyledCard>
          <CardContent sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
              <Typography variant="h6" component="div" sx={{ lineHeight: 1.2 }}>{g.name}</Typography>
              <Chip label={status.label} color={status.color} size="small" />
            </Box>
            <Typography variant="body2" color="text.secondary">
              {g.year}
              {g.budget ? ` · ${formatBudget(g.budget)} budget` : ''}
              {g.exchangeDate ? ` · ${formatShortDate(g.exchangeDate)}` : ''}
            </Typography>

            {mode === 'participant' && (
              <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
                {g.matchReady ? (
                  <Chip
                    size="small"
                    icon={g.matchViewed ? <Visibility /> : <VisibilityOff />}
                    label={g.matchViewed ? 'Match revealed' : 'Your match is ready!'}
                    color={g.matchViewed ? 'default' : 'secondary'}
                  />
                ) : (
                  <Chip size="small" label="Waiting for the draw" variant="outlined" />
                )}
                {g.matchReady && g.giftIdeaCount > 0 && (
                  <Chip size="small" icon={<Lightbulb />} label={`${g.giftIdeaCount} gift idea${g.giftIdeaCount === 1 ? '' : 's'}`} />
                )}
              </Stack>
            )}
            {mode === 'organizer' && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {g.participantCount ?? 0} participant{g.participantCount === 1 ? '' : 's'}
              </Typography>
            )}
          </CardContent>
          <CardActions sx={{ px: 2, pb: 2 }}>
            {mode === 'participant' ? (
              <Button fullWidth variant="contained" startIcon={<CardGiftcard />} onClick={() => navigate(`/g/${g.id}`)}>
                Open
              </Button>
            ) : (
              <Button fullWidth variant="outlined" startIcon={<ManageAccounts />} onClick={() => navigate(`/group/${g.id}`)}>
                Manage
              </Button>
            )}
          </CardActions>
        </StyledCard>
      </Grid>
    );
  };

  return (
    <Layout title="My Secret Santas" loading={loading}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h2" sx={{ color: 'primary.main', fontSize: { xs: '2.4rem', md: '3rem' } }}>
          My Secret Santas
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      {groups.length === 0 && (
        <Box sx={{ py: 4, px: 3, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ fontFamily: 'inherit' }}>You're not in any Secret Santa yet</Typography>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            If someone added you, make sure you signed in with the same email they used.
            Or start a group of your own.
          </Typography>
        </Box>
      )}

      {participating.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2, fontFamily: 'inherit' }}>I'm in</Typography>
          <Grid container spacing={3}>{participating.map((g) => renderGroup(g, 'participant'))}</Grid>
        </Box>
      )}

      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h5" sx={{ fontFamily: 'inherit' }}>I organize</Typography>
          <Button variant="contained" color="secondary" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
            New group
          </Button>
        </Box>
        {organizing.length > 0 ? (
          <Grid container spacing={3}>{organizing.map((g) => renderGroup(g, 'organizer'))}</Grid>
        ) : (
          <Typography color="text.secondary">
            Organize a Secret Santa for your family.
          </Typography>
        )}
      </Box>

      <Dialog open={createOpen} onClose={() => !creating && setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Secret Santa group</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              autoFocus label="Group name" fullWidth value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Lyon Family"
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Year" type="number" fullWidth value={form.year}
                onChange={(e) => {
                  const year = parseInt(e.target.value) || form.year;
                  const next = { ...form, year };
                  // Keep Christmas Day in sync when the year changes and the date is still Christmas.
                  if (!form.exchangeDate || form.exchangeDate.endsWith('-12-25')) {
                    next.exchangeDate = `${year}-12-25`;
                  }
                  setForm(next);
                }}
              />
              <TextField
                label="Budget ($)" type="number" fullWidth value={form.budget}
                onChange={(e) => setForm({ ...form, budget: e.target.value })}
                placeholder="optional"
              />
            </Stack>
            <TextField
              label="Exchange date" type="date" fullWidth value={form.exchangeDate}
              onChange={(e) => setForm({ ...form, exchangeDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText="Defaults to Christmas Day."
            />
            <TextField
              label="Where" fullWidth value={form.exchangeLocation}
              onChange={(e) => setForm({ ...form, exchangeLocation: e.target.value })}
              placeholder="e.g. Grandma's house (optional)"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={creating}>
            {creating ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default Dashboard;
